import { useState } from 'react';
import { RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminFileBulk, useAdminFiles } from '../../api/hooks';
import { formatBytes, formatDateTime } from '../../lib/format';
import { Thumb } from '../../components/Thumb';
import {
  Button,
  Checkbox,
  EmptyState,
  Input,
  Pager,
  Pill,
  Select,
  Spinner,
  useConfirm,
} from '../../components/ui';
import type { SortKey } from '../../api/types';

const STATES = [
  { value: 'active', label: 'Active' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'all', label: 'Everything' },
];

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'largest', label: 'Largest first' },
  { value: 'name', label: 'Name' },
];

export function AdminFiles() {
  const [q, setQ] = useState('');
  const [state, setState] = useState('active');
  const [sort, setSort] = useState<SortKey>('newest');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading } = useAdminFiles({ q, sort, page, state });
  const bulk = useAdminFileBulk();
  const confirm = useConfirm();

  const files = data?.items ?? [];
  const allSelected = files.length > 0 && files.every((file) => selected.has(file.id));

  const act = async (action: 'soft-delete' | 'restore' | 'purge') => {
    if (selected.size === 0) return;
    if (action === 'purge') {
      const ok = await confirm({
        title: `Permanently delete ${selected.size} file${selected.size === 1 ? '' : 's'}?`,
        body: 'The bytes are erased from disk. This cannot be undone.',
        confirmLabel: 'Erase permanently',
      });
      if (!ok) return;
    }
    try {
      const result = await bulk.mutateAsync({ fileIds: [...selected], action });
      setSelected(new Set());
      toast.success(`${result.affected} file${result.affected === 1 ? '' : 's'} updated`);
    } catch (error) {
      toast.error('Could not do that', { description: (error as Error).message });
    }
  };

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-line bg-surface px-3 py-3 sm:px-4">
        <h1 className="text-lg font-semibold">All files</h1>
        <p className="mt-0.5 text-sm text-ink-dim">
          Everyone's files, including deleted ones. Sharing rules do not apply here.
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          <Input
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setPage(1);
            }}
            placeholder="Search filenames, descriptions and paths…"
            aria-label="Search"
            className="min-w-0 flex-1 sm:max-w-sm"
          />
          <Select ariaLabel="State" value={state} onChange={(next) => { setState(next); setPage(1); }} options={STATES} />
          <Select ariaLabel="Sort" value={sort} onChange={(next) => { setSort(next); setPage(1); }} options={SORTS} />
        </div>

        {selected.size > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-brand/40 bg-brand/8 px-3 py-2">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <div className="flex-1" />
            <Button onClick={() => act('soft-delete')}>
              <Trash2 className="size-4" aria-hidden />
              Soft delete
            </Button>
            <Button onClick={() => act('restore')}>
              <RotateCcw className="size-4" aria-hidden />
              Restore
            </Button>
            <Button variant="danger" onClick={() => act('purge')}>
              Erase permanently
            </Button>
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <Spinner />
      ) : files.length === 0 ? (
        <EmptyState title="No files matched" />
      ) : (
        <>
          <div className="border-b border-line px-3 py-2 sm:px-4">
            <Checkbox
              checked={allSelected}
              indeterminate={selected.size > 0 && !allSelected}
              onCheckedChange={(next) =>
                setSelected(next ? new Set(files.map((file) => file.id)) : new Set())
              }
              label={<span className="text-sm text-ink-dim">Select all on this page</span>}
            />
          </div>
          <ul className="flex-1 divide-y divide-line">
            {files.map((file) => (
              <li key={file.id} className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
                <Checkbox
                  checked={selected.has(file.id)}
                  onCheckedChange={() =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (next.has(file.id)) next.delete(file.id);
                      else next.add(file.id);
                      return next;
                    })
                  }
                />
                <Thumb file={file} size={96} className="size-10 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate font-medium">
                    {file.name}
                    {file.deletedAt ? <Pill tone="warn">deleted</Pill> : null}
                  </p>
                  <p className="truncate text-xs text-ink-dim">
                    {file.ownerName} · {formatBytes(file.sizeBytes)} ·{' '}
                    {formatDateTime(file.createdAt)}
                    {file.folderPath ? ` · ${file.folderPath}` : ''}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {data ? (
        <Pager page={data.page} total={data.total} pageSize={data.pageSize} onPage={setPage} />
      ) : null}
    </div>
  );
}
